import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  Autocomplete,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PatternIcon from '@mui/icons-material/DataObject';
import AssessmentIcon from '@mui/icons-material/Assessment';

import config from '../../config';
import { useAppContext } from '../AppContext';
import { ConcepteIcon } from '../common/IconMap';
import ChipListEditor from './ChipListEditor';
import ReglesAssignacioSection from './ReglesAssignacioSection';
import AmazonReportModal from '../common/AmazonReportModal';

/**
 * AutoconcepteSection
 * Permet visualitzar i modificar tant el mapeig directe (mov-to-concepte),
 * el mapeig per expressions regulars (mov-to-concepte-pattern) i les
 * regles avançades d'assignació (regles-assignacio).
 * Incorpora Mode Lectura per defecte (sense esborrats accidentals) i
 * Mode Edició protegit mitjançant la icona de llapis amb botons Desar i Cancel·lar.
 */
const AutoconcepteSection = ({
  autoconcepte = {},
  onChange,
  conceptesList = [],
  disabled = false,
  userapp = '',
  onNotification = null
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { ctxUser } = useAppContext();
  const jwtToken = ctxUser?.fdvToken?.jwtToken || '';

  const movToConcepte = autoconcepte?.['mov-to-concepte'] || autoconcepte?.movToConcepte || {};
  const movToConceptePattern = autoconcepte?.['mov-to-concepte-pattern'] || autoconcepte?.movToConceptePattern || {};
  const reglesAssignacio = autoconcepte?.['regles-assignacio'] || autoconcepte?.reglesAssignacio || {};

  // Estat per als diàlegs de creació de nou grup / regla
  const [openNewDirectDialog, setOpenNewDirectDialog] = useState(false);
  const [newDirectConceptName, setNewDirectConceptName] = useState('');

  const [openNewPatternDialog, setOpenNewPatternDialog] = useState(false);
  const [newPatternConceptName, setNewPatternConceptName] = useState('');
  const [newPatternRegex, setNewPatternRegex] = useState('');

  // Mode Edició aïllat per a Mapeig Directe (Bloc 1)
  const [editingDirectKey, setEditingDirectKey] = useState(null);
  const [draftDirectMoviments, setDraftDirectMoviments] = useState([]);
  const [expandedDirectKeys, setExpandedDirectKeys] = useState({});

  // Mode Edició aïllat per a Mapeig Regex (Bloc 2)
  const [editingPatternKey, setEditingPatternKey] = useState(null);
  const [draftPatterns, setDraftPatterns] = useState([]);
  const [expandedPatternKeys, setExpandedPatternKeys] = useState({});

  // Estat de re-avaluació manual global a l'inici de la pestanya
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [localSnackbar, setLocalSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [amazonReport, setAmazonReport] = useState({ open: false, reportHtml: '', reportSummary: '', updatedCount: 0 });

  const notify = (message, severity = 'info') => {
    if (onNotification) {
      onNotification(message, severity);
    } else {
      setLocalSnackbar({ open: true, message, severity });
    }
  };

  // Re-avaluar pendents amb TOTES les regles d'autoconcepte
  const handleReevaluateAllPendents = async () => {
    const targetUserapp = userapp || '';
    if (!targetUserapp) {
      notify('No s’ha pogut identificar el perfil d’usuari per re-avaluar.', 'warning');
      return;
    }

    try {
      setIsReevaluating(true);
      const headers = jwtToken ? { Authorization: `${jwtToken}` } : {};
      const res = await axios.get(
        `${config.apiBaseUrl}/v1/syncbanc/updateconceptespendents/${targetUserapp}`,
        { headers }
      );

      const data = res.data;
      let count = 0;
      let reportHtml = '';
      let reportSummary = '';

      if (typeof data === 'object' && data !== null) {
        count = Number(data.updatedCount) || 0;
        reportHtml = data.reportHtml || '';
        reportSummary = data.reportSummary || '';
      } else {
        const parsed = parseInt(data, 10);
        count = isNaN(parsed) ? 0 : parsed;
      }

      if (count > 0) {
        notify(`S'han re-avaluat i actualitzat ${count} moviment(s) pendents amb les regles d'autoconcepte!`, 'success');
      } else {
        notify('Re-avaluació completada: no s’han trobat nous moviments pendents que compleixin les regles.', 'info');
      }

      if (reportHtml || reportSummary) {
        setAmazonReport({
          open: true,
          reportHtml,
          reportSummary,
          updatedCount: count
        });
      }
    } catch (err) {
      console.error('Error al re-avaluar moviments pendents:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Error en connectar amb el servidor';
      notify(`Error al re-avaluar pendents: ${errorMsg}`, 'error');
    } finally {
      setIsReevaluating(false);
    }
  };

  // Llista de noms de conceptes per a l'autocomplete
  const conceptOptions = (conceptesList || []).map((c) => (typeof c === 'string' ? c : c.nom)).filter(Boolean);

  // Helper per generar claus normalitzades (kebab-case)
  const toKey = (name) => {
    return name
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // --- Handlers Mapeig Directe (Bloc 1) ---
  const handleDirectMovimentsChange = (groupKey, newMoviments) => {
    const currentGroup = movToConcepte[groupKey] || {};
    const updatedGroup = {
      ...currentGroup,
      moviments: newMoviments
    };

    const updatedDirect = {
      ...movToConcepte,
      [groupKey]: updatedGroup
    };

    onChange({
      ...autoconcepte,
      'mov-to-concepte': updatedDirect
    });
  };

  const handleStartEditDirect = (key, currentMoviments) => {
    setEditingDirectKey(key);
    setDraftDirectMoviments(Array.isArray(currentMoviments) ? [...currentMoviments] : []);
    setExpandedDirectKeys((prev) => ({ ...prev, [key]: true }));
  };

  const handleSaveEditDirect = (key) => {
    handleDirectMovimentsChange(key, draftDirectMoviments);
    setEditingDirectKey(null);
    setDraftDirectMoviments([]);
  };

  const handleCancelEditDirect = () => {
    setEditingDirectKey(null);
    setDraftDirectMoviments([]);
  };

  const handleDeleteDirectGroup = (key) => {
    const updatedDirect = { ...movToConcepte };
    delete updatedDirect[key];
    onChange({
      ...autoconcepte,
      'mov-to-concepte': updatedDirect
    });
  };

  const handleAddDirectGroup = () => {
    const name = newDirectConceptName.trim();
    if (!name) return;

    let key = toKey(name);
    if (!key) key = `concepte-${Date.now()}`;
    if (movToConcepte[key]) {
      key = `${key}-${Date.now()}`;
    }

    const updatedDirect = {
      ...movToConcepte,
      [key]: {
        nom: name.toUpperCase(),
        moviments: []
      }
    };

    onChange({
      ...autoconcepte,
      'mov-to-concepte': updatedDirect
    });

    setNewDirectConceptName('');
    setOpenNewDirectDialog(false);
  };

  // --- Handlers Mapeig Regex (Bloc 2) ---
  const handlePatternListChange = (ruleKey, newPatterns) => {
    const currentRule = movToConceptePattern[ruleKey] || {};
    const updatedRule = {
      ...currentRule,
      patterns: newPatterns
    };

    const updatedPatterns = {
      ...movToConceptePattern,
      [ruleKey]: updatedRule
    };

    onChange({
      ...autoconcepte,
      'mov-to-concepte-pattern': updatedPatterns
    });
  };

  const handleStartEditPattern = (key, currentPatterns) => {
    setEditingPatternKey(key);
    setDraftPatterns(Array.isArray(currentPatterns) ? [...currentPatterns] : []);
    setExpandedPatternKeys((prev) => ({ ...prev, [key]: true }));
  };

  const handleSaveEditPattern = (key) => {
    handlePatternListChange(key, draftPatterns);
    setEditingPatternKey(null);
    setDraftPatterns([]);
  };

  const handleCancelEditPattern = () => {
    setEditingPatternKey(null);
    setDraftPatterns([]);
  };

  const handleDeletePatternRule = (key) => {
    const updatedPatterns = { ...movToConceptePattern };
    delete updatedPatterns[key];
    onChange({
      ...autoconcepte,
      'mov-to-concepte-pattern': updatedPatterns
    });
  };

  const handleAddPatternRule = () => {
    const name = newPatternConceptName.trim();
    if (!name) return;

    let key = toKey(name);
    if (!key) key = `regla-${Date.now()}`;
    if (movToConceptePattern[key]) {
      key = `${key}-${Date.now()}`;
    }

    const patterns = newPatternRegex.trim() ? [newPatternRegex.trim()] : [];

    const updatedPatterns = {
      ...movToConceptePattern,
      [key]: {
        nom: name.toUpperCase(),
        patterns: patterns
      }
    };

    onChange({
      ...autoconcepte,
      'mov-to-concepte-pattern': updatedPatterns
    });

    setNewPatternConceptName('');
    setNewPatternRegex('');
    setOpenNewPatternDialog(false);
  };

  // --- Handlers Regles Avançades d'Assignació (Bloc 3) ---
  const handleReglesAssignacioChange = (newRules) => {
    onChange({
      ...autoconcepte,
      'regles-assignacio': newRules
    });
  };

  const directKeys = Object.keys(movToConcepte || {});
  const patternKeys = Object.keys(movToConceptePattern || {});

  return (
    <Box sx={{ mt: 2 }}>
      {/* ============================================================ */}
      {/* Capçalera i Botó Global de Re-avaluació a l'Inici de Pestanya */}
      {/* ============================================================ */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 2,
          bgcolor: '#f8fafc',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', md: '70%' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHighIcon color="primary" sx={{ fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
              Motor d'Autoconcepte
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Defineix com assignar automàticament conceptes als nous moviments mitjançant mapeig directe, expressions regulars o regles avançades combinades.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {(amazonReport.reportHtml || amazonReport.reportSummary) && (
            <Button
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<AssessmentIcon />}
              onClick={() => setAmazonReport(prev => ({ ...prev, open: true }))}
              sx={{ fontWeight: 600 }}
            >
              Veure informe d'Amazon
            </Button>
          )}

          <Tooltip title="Executa el motor complet ConcepteMapper sobre les transaccions de la BD en estat PENDENT aplicant totes les regles (directes, patrons i avançades)">
            <span>
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                startIcon={isReevaluating ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
                onClick={handleReevaluateAllPendents}
                disabled={disabled || isReevaluating}
                sx={{ fontWeight: 700, minWidth: { xs: '100%', sm: 220 } }}
              >
                {isReevaluating ? 'Re-avaluant pendents...' : 'Re-avaluar pendents ara'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* ============================================================ */}
      {/* 1. Mapeig Directe (mov-to-concepte)                           */}
      {/* ============================================================ */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
        <CardHeader
          avatar={<PsychologyIcon color="secondary" sx={{ fontSize: 28 }} />}
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              1. Mapeig Directe (mov-to-concepte)
            </Typography>
          }
          subheader="Assignació directa de moviments literals a un concepte (ex. PENDENT, PDT DESGLOSSAR)."
          action={
            !disabled && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenNewDirectDialog(true)}
                sx={{ mr: { xs: 0, sm: 1 }, mt: 1, width: { xs: '100%', sm: 'auto' } }}
              >
                Nou Grup Concepte
              </Button>
            )
          }
          sx={{
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            flexWrap: 'wrap',
            '& .MuiCardHeader-action': { m: 0, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {directKeys.length === 0 ? (
            <Alert severity="info">No hi ha grups de mapeig directe definits.</Alert>
          ) : isMobile ? (
            /* Vista Mòbil: Acordions */
            directKeys.map((key) => {
              const group = movToConcepte[key] || {};
              const groupName = group.nom || (typeof key === 'string' ? key.toUpperCase() : `Grup ${key}`);
              const moviments = Array.isArray(group.moviments)
                ? group.moviments
                : (typeof group.moviments === 'string' && group.moviments.trim() ? [group.moviments.trim()] : []);
              const isEditing = editingDirectKey === key;

              return (
                <Accordion
                  key={key}
                  expanded={isEditing || Boolean(expandedDirectKeys[key])}
                  onChange={(_, isExp) => {
                    if (!isEditing) {
                      setExpandedDirectKeys((prev) => ({ ...prev, [key]: isExp }));
                    }
                  }}
                  sx={{
                    mb: 1.5,
                    border: isEditing ? '1.5px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '8px !important',
                    boxShadow: isEditing ? '0 2px 8px rgba(25,118,210,0.1)' : 'none',
                    backgroundColor: isEditing ? '#f8faff' : '#ffffff',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={isEditing ? null : <ExpandMoreIcon />}
                    sx={{
                      backgroundColor: isEditing ? '#eef5fc' : '#fcfcfc',
                      '&:hover': { backgroundColor: isEditing ? '#eef5fc' : '#f5f5f5' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConcepteIcon concept={groupName} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: '#eef2f6', px: 1, py: 0.2, borderRadius: 1 }}>
                          {moviments.length} moviments
                        </Typography>
                      </Box>
                      {!disabled && !isEditing && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Editar regla">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEditDirect(key, moviments);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar grup de concepte">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteDirectGroup(key);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                    {isEditing ? (
                      /* Mode Edició Mòbil */
                      <Box sx={{ width: '100%' }}>
                        <ChipListEditor
                          label={`Editant moviments per a "${groupName}"`}
                          helperText="Pots afegir nous moviments o eliminar-ne prement la creueta del xip."
                          items={draftDirectMoviments}
                          onChange={(newList) => setDraftDirectMoviments(newList)}
                          placeholder="Escriu el text exacte del moviment i prem Enter..."
                          disabled={disabled}
                          color="secondary"
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleCancelEditDirect}
                          >
                            Cancel·lar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveEditDirect(key)}
                          >
                            Guardar
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      /* Mode Lectura Mòbil (Xips sense creueta) */
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center' }}>
                        {moviments.length === 0 ? (
                          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            Sense moviments assignats
                          </Typography>
                        ) : (
                          moviments.map((m, idx) => (
                            <Chip
                              key={`${m}-${idx}`}
                              label={m}
                              color="secondary"
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          ))
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            /* Vista Escriptori: Taula amb Mode Lectura per defecte i Edició Inline */
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 220 }}>Concepte Destí</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Moviments Assignats</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 140, textAlign: 'center' }}>Accions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {directKeys.map((key) => {
                    const group = movToConcepte[key] || {};
                    const groupName = group.nom || (typeof key === 'string' ? key.toUpperCase() : `Grup ${key}`);
                    const moviments = Array.isArray(group.moviments)
                      ? group.moviments
                      : (typeof group.moviments === 'string' && group.moviments.trim() ? [group.moviments.trim()] : []);
                    const isEditing = editingDirectKey === key;

                    return (
                      <TableRow
                        key={key}
                        hover={!isEditing}
                        sx={{
                          backgroundColor: isEditing ? '#f0f7ff' : 'inherit',
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ConcepteIcon concept={groupName} />
                          </Box>
                        </TableCell>

                        <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                          {isEditing ? (
                            /* Mode Edició Escriptori */
                            <Box sx={{ width: '100%' }}>
                              <ChipListEditor
                                label={`Editant moviments per a "${groupName}"`}
                                helperText="Pots afegir nous moviments o eliminar-ne prement la creueta del xip."
                                items={draftDirectMoviments}
                                onChange={(newList) => setDraftDirectMoviments(newList)}
                                placeholder="Escriu el text exacte del moviment i prem Enter..."
                                disabled={disabled}
                                color="secondary"
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<CloseIcon />}
                                  onClick={handleCancelEditDirect}
                                >
                                  Cancel·lar
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleSaveEditDirect(key)}
                                >
                                  Guardar
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            /* Mode Lectura Escriptori (Xips segurs sense creueta d'esborrat) */
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center', minHeight: 32 }}>
                              {moviments.length === 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                  Sense moviments assignats
                                </Typography>
                              ) : (
                                moviments.map((m, idx) => (
                                  <Chip
                                    key={`${m}-${idx}`}
                                    label={m}
                                    color="secondary"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                ))
                              )}
                            </Box>
                          )}
                        </TableCell>

                        <TableCell align="center" sx={{ verticalAlign: 'top', pt: 2 }}>
                          {!disabled && (
                            isEditing ? (
                              <Chip label="Editant..." color="primary" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title="Editar regla">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleStartEditDirect(key, moviments)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar grup de concepte">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteDirectGroup(key)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* 2. Mapeig per Patrons Regex (mov-to-concepte-pattern)         */}
      {/* ============================================================ */}
      <Card variant="outlined" sx={{ mb: 4, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
        <CardHeader
          avatar={<PatternIcon color="primary" sx={{ fontSize: 28 }} />}
          title={
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              2. Mapeig per Patrons Regex (mov-to-concepte-pattern)
            </Typography>
          }
          subheader="Regles d'assignació mitjançant expressions regulars (ex. GAS, NOMINA, APPS, .*AMAZON.*)."
          action={
            !disabled && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenNewPatternDialog(true)}
                sx={{ mr: { xs: 0, sm: 1 }, mt: 1, width: { xs: '100%', sm: 'auto' } }}
              >
                Nova Regla Regex
              </Button>
            )
          }
          sx={{
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #f0f0f0',
            flexWrap: 'wrap',
            '& .MuiCardHeader-action': { m: 0, mt: { xs: 1, sm: 0 }, width: { xs: '100%', sm: 'auto' } }
          }}
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          {patternKeys.length === 0 ? (
            <Alert severity="info">No hi ha regles de patrons definides.</Alert>
          ) : isMobile ? (
            /* Vista Mòbil: Acordions */
            patternKeys.map((key) => {
              const rule = movToConceptePattern[key] || {};
              const ruleName = rule.nom || (typeof key === 'string' ? key.toUpperCase() : `Regla ${key}`);
              const patterns = Array.isArray(rule.patterns)
                ? rule.patterns
                : (typeof rule.patterns === 'string' && rule.patterns.trim() ? [rule.patterns.trim()] : []);
              const isEditing = editingPatternKey === key;

              return (
                <Accordion
                  key={key}
                  expanded={isEditing || Boolean(expandedPatternKeys[key])}
                  onChange={(_, isExp) => {
                    if (!isEditing) {
                      setExpandedPatternKeys((prev) => ({ ...prev, [key]: isExp }));
                    }
                  }}
                  sx={{
                    mb: 1.5,
                    border: isEditing ? '1.5px solid #1976d2' : '1px solid #e0e0e0',
                    borderRadius: '8px !important',
                    boxShadow: isEditing ? '0 2px 8px rgba(25,118,210,0.1)' : 'none',
                    backgroundColor: isEditing ? '#f8faff' : '#ffffff',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={isEditing ? null : <ExpandMoreIcon />}
                    sx={{
                      backgroundColor: isEditing ? '#eef5fc' : '#fcfcfc',
                      '&:hover': { backgroundColor: isEditing ? '#eef5fc' : '#f5f5f5' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ConcepteIcon concept={ruleName} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: '#e3f2fd', px: 1, py: 0.2, borderRadius: 1 }}>
                          {patterns.length} patrons
                        </Typography>
                      </Box>
                      {!disabled && !isEditing && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Editar regla">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEditPattern(key, patterns);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar regla">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePatternRule(key);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                    {isEditing ? (
                      /* Mode Edició Mòbil */
                      <Box sx={{ width: '100%' }}>
                        <ChipListEditor
                          label={`Editant patrons per a "${ruleName}"`}
                          helperText="Pots afegir noves expressions regulars o eliminar-ne prement la creueta del xip."
                          items={draftPatterns}
                          onChange={(newList) => setDraftPatterns(newList)}
                          placeholder="Escriu un patró regex (ex. .*MERCADONA.*) i prem Enter..."
                          disabled={disabled}
                          color="primary"
                          isMonospace={true}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleCancelEditPattern}
                          >
                            Cancel·lar
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveEditPattern(key)}
                          >
                            Guardar
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      /* Mode Lectura Mòbil (Xips segurs) */
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center' }}>
                        {patterns.length === 0 ? (
                          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            Sense patrons definits
                          </Typography>
                        ) : (
                          patterns.map((p, idx) => (
                            <Chip
                              key={`${p}-${idx}`}
                              label={p}
                              color="primary"
                              size="small"
                              sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                            />
                          ))
                        )}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : (
            /* Vista Escriptori: Taula amb Mode Lectura per defecte i Edició Inline */
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 220 }}>Concepte / Regla</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Patrons d'Expressió Regular (Regex)</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 140, textAlign: 'center' }}>Accions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patternKeys.map((key) => {
                    const rule = movToConceptePattern[key] || {};
                    const ruleName = rule.nom || (typeof key === 'string' ? key.toUpperCase() : `Regla ${key}`);
                    const patterns = Array.isArray(rule.patterns)
                      ? rule.patterns
                      : (typeof rule.patterns === 'string' && rule.patterns.trim() ? [rule.patterns.trim()] : []);
                    const isEditing = editingPatternKey === key;

                    return (
                      <TableRow
                        key={key}
                        hover={!isEditing}
                        sx={{
                          backgroundColor: isEditing ? '#f0f7ff' : 'inherit',
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ verticalAlign: 'top', pt: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ConcepteIcon concept={ruleName} />
                          </Box>
                        </TableCell>

                        <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                          {isEditing ? (
                            /* Mode Edició Escriptori */
                            <Box sx={{ width: '100%' }}>
                              <ChipListEditor
                                label={`Editant patrons per a "${ruleName}"`}
                                helperText="Pots afegir noves expressions regulars o eliminar-ne prement la creueta del xip."
                                items={draftPatterns}
                                onChange={(newList) => setDraftPatterns(newList)}
                                placeholder="Escriu un patró regex i prem Enter..."
                                disabled={disabled}
                                color="primary"
                                isMonospace={true}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  startIcon={<CloseIcon />}
                                  onClick={handleCancelEditPattern}
                                >
                                  Cancel·lar
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<SaveIcon />}
                                  onClick={() => handleSaveEditPattern(key)}
                                >
                                  Guardar
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            /* Mode Lectura Escriptori (Xips segurs) */
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, alignItems: 'center', minHeight: 32 }}>
                              {patterns.length === 0 ? (
                                <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                                  Sense patrons definits
                                </Typography>
                              ) : (
                                patterns.map((p, idx) => (
                                  <Chip
                                    key={`${p}-${idx}`}
                                    label={p}
                                    color="primary"
                                    size="small"
                                    sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                  />
                                ))
                              )}
                            </Box>
                          )}
                        </TableCell>

                        <TableCell align="center" sx={{ verticalAlign: 'top', pt: 2 }}>
                          {!disabled && (
                            isEditing ? (
                              <Chip label="Editant..." color="primary" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                            ) : (
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Tooltip title="Editar regla">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleStartEditPattern(key, patterns)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar regla">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeletePatternRule(key)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ============================================================ */}
      {/* 3. Regles Avançades d'Assignació (regles-assignacio)          */}
      {/* ============================================================ */}
      <ReglesAssignacioSection
        rules={reglesAssignacio}
        onChange={handleReglesAssignacioChange}
        conceptesList={conceptesList}
        disabled={disabled}
      />

      {/* Diàleg Afegir Nou Grup Directe */}
      <Dialog
        open={openNewDirectDialog}
        onClose={() => setOpenNewDirectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Afegir Nou Grup de Concepte Directe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Indica el concepte que rebrà els moviments assignats:
          </Typography>
          <Autocomplete
            freeSolo
            options={conceptOptions}
            value={newDirectConceptName}
            onInputChange={(_, val) => setNewDirectConceptName(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nom del Concepte"
                placeholder="Ex. PENDENT, HIPOTECA..."
                fullWidth
                size="small"
                autoFocus
              />
            )}
          />
          <Alert severity="info" variant="outlined" sx={{ mt: 2, fontSize: '0.82rem' }}>
            En afegir-lo, s'incorporarà a la pantalla. Recorda prémer el botó <strong>"Guardar Canvis"</strong> per persistir-ho a la base de dades.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenNewDirectDialog(false)}>Cancel·lar</Button>
          <Button
            variant="contained"
            onClick={handleAddDirectGroup}
            disabled={!newDirectConceptName.trim()}
          >
            Crear Grup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diàleg Afegir Nova Regla Regex */}
      <Dialog
        open={openNewPatternDialog}
        onClose={() => setOpenNewPatternDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Afegir Nova Regla Regex</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Crea una regla de patró indicant el concepte destí i un patró opcional inicial:
          </Typography>
          <Autocomplete
            freeSolo
            options={conceptOptions}
            value={newPatternConceptName}
            onInputChange={(_, val) => setNewPatternConceptName(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nom del Concepte"
                placeholder="Ex. GAS, NOMINA, SUPERMERCAT..."
                fullWidth
                size="small"
                autoFocus
                sx={{ mb: 2 }}
              />
            )}
          />
          <TextField
            label="Patró Regex inicial (opcional)"
            placeholder="Ex. .*AMAZON.* o GAS POWER.*"
            value={newPatternRegex}
            onChange={(e) => setNewPatternRegex(e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiInputBase-root': { fontFamily: 'monospace' }
            }}
          />
          <Alert severity="info" variant="outlined" sx={{ mt: 2, fontSize: '0.82rem' }}>
            En crear-la, s'incorporarà a la pantalla. Recorda prémer el botó <strong>"Guardar Canvis"</strong> per persistir-ho a la base de dades.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenNewPatternDialog(false)}>Cancel·lar</Button>
          <Button
            variant="contained"
            onClick={handleAddPatternRule}
            disabled={!newPatternConceptName.trim()}
          >
            Crear Regla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar local si no hi ha notificacions globals */}
      <Snackbar
        open={localSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setLocalSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setLocalSnackbar((prev) => ({ ...prev, open: false }))}
          severity={localSnackbar.severity}
          variant="filled"
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {localSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal d'Informe de Reconciliació d'Amazon */}
      <AmazonReportModal
        open={amazonReport.open}
        onClose={() => setAmazonReport(prev => ({ ...prev, open: false }))}
        reportHtml={amazonReport.reportHtml}
        reportSummary={amazonReport.reportSummary}
        updatedCount={amazonReport.updatedCount}
      />
    </Box>
  );
};

export default AutoconcepteSection;
